import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import api, { authApi } from '../services/api';

function ProfileSettings() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Password change form
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await authApi.getCurrentUser();
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Error fetching user:', err);
      setError('Failed to load user information');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    setPasswordData({
      ...passwordData,
      [e.target.name]: e.target.value
    });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setError('');
    setSuccess('');

    // Validate passwords match
    if (passwordData.new_password !== passwordData.confirm_password) {
      setError('New passwords do not match');
      setPasswordLoading(false);
      return;
    }

    // Validate password strength
    if (passwordData.new_password.length < 8) {
      setError('New password must be at least 8 characters long');
      setPasswordLoading(false);
      return;
    }

    try {
      await authApi.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });

      setSuccess('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleLogoutAllDevices = async () => {
    setError('');
    setSuccess('');
    setLogoutAllLoading(true);
    try {
      await authApi.logoutAllDevices();
      api.clearStoredAuth();
      setSuccess('All devices have been signed out. Please sign in again.');
      navigate('/login');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to logout all devices');
    } finally {
      setLogoutAllLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Profile Settings
      </Typography>

      <Grid container spacing={3}>
        {/* User Information Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">User Information</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              
              {currentUser && (
                <>
                  <Typography variant="body1" gutterBottom>
                    <strong>Name:</strong> {currentUser.name}
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    <strong>Email:</strong> {currentUser.email}
                  </Typography>
                  {currentUser.created_date && (
                    <Typography variant="body1" gutterBottom>
                      <strong>Member Since:</strong> {new Date(currentUser.created_date).toLocaleDateString('en-GB')}
                    </Typography>
                  )}
                  {currentUser.last_login_date && (
                    <Typography variant="body1">
                      <strong>Last Login:</strong> {new Date(currentUser.last_login_date).toLocaleString()}
                    </Typography>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Change Password Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Change Password</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              <form onSubmit={handlePasswordSubmit}>
                <TextField
                  fullWidth
                  type="password"
                  label="Current Password"
                  name="current_password"
                  value={passwordData.current_password}
                  onChange={handlePasswordChange}
                  margin="normal"
                  required
                />
                <TextField
                  fullWidth
                  type="password"
                  label="New Password"
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  margin="normal"
                  required
                  helperText="Password must be at least 8 characters long"
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm New Password"
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  margin="normal"
                  required
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2 }}
                  disabled={passwordLoading}
                  startIcon={passwordLoading ? <CircularProgress size={20} /> : <LockIcon />}
                >
                  {passwordLoading ? 'Changing Password...' : 'Change Password'}
                </Button>

                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  sx={{ mt: 2 }}
                  disabled={logoutAllLoading}
                  onClick={handleLogoutAllDevices}
                >
                  {logoutAllLoading ? 'Logging out all devices...' : 'Logout All Devices'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default ProfileSettings;